import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { JobDetailTab, JobDocumentsPhotosTab, JobNotificationsTab, JobAdditionalWorksTab, } from "./components";
import { PageHeader, PageHeaderWithBack } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { useJob, useContacts } from "./hooks";
import { useMessagesUnreadBadge } from "@/hooks/useMessagesUnreadBadge";
import { sitesService } from "@/api/services/sites.service";
import { jobsService } from "@/api/services/jobs.service";
import { documentsService, } from "@/api/services/documents.service";
import { dilapidationService } from "@/api/services/dilapidation.service";
import styles from "./JobEdit.module.css";
const BASE_TABS = [
    { id: "detail", label: "Job Detail" },
    { id: "documents", label: "Documents and Photos" },
    { id: "notifications", label: "Messages" },
];
export const JobEdit = () => {
    const { jobId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const basePath = location.pathname.startsWith("/staff") ? "/staff" : "/admin";
    const { job, loading: jobLoading, saving: jobSaving, error: jobError, updateJob, updateTradeAssignment, refetch: refetchJob, } = useJob(jobId);
    const { contacts: allContacts, refetch: refetchContacts } = useContacts("all", "", job?.contactId ? [job.contactId] : []);
    const { contacts: surveyorContacts } = useContacts("contactType", "", [], undefined, "surveyor");
    const [activeTab, setActiveTab] = useState("detail");
    const { unreadCount, clearBadge } = useMessagesUnreadBadge({
        type: "job",
        entityId: jobId ?? "",
        contactId: job?.contactId ?? "",
    });
    useEffect(() => {
        const tabFromState = location.state?.tab;
        if (tabFromState && ["detail", "documents", "notifications", "additional-works"].includes(tabFromState)) {
            setActiveTab(tabFromState);
        }
    }, [location.state?.tab]);
    const hasAdditionalWorks = job?.isAdditionalWorks ?? job?.isAdditionalWork ?? false;
    const [openAddDocumentRequest, setOpenAddDocumentRequest] = useState(null);
    const [availableSites, setAvailableSites] = useState([]);
    const [sitesLoading, setSitesLoading] = useState(false);
    const [siteDocuments, setSiteDocuments] = useState([]);
    const [jobDocuments, setJobDocuments] = useState([]);
    const [jobDocumentsFromDocumentsApi, setJobDocumentsFromDocumentsApi] = useState([]);
    const [quoteDocuments, setQuoteDocuments] = useState([]);
    const [checklistItems, setChecklistItems] = useState([]);
    const [checklistLoading, setChecklistLoading] = useState(false);
    const [tradeDateChangeRequests, setTradeDateChangeRequests] = useState([]);
    const [tradeDateChangeRequestsLoading, setTradeDateChangeRequestsLoading] = useState(false);
    const [hasDilapidation, setHasDilapidation] = useState(false);
    const fetchTradeDateChangeRequests = useCallback(async () => {
        if (!jobId) {
            setTradeDateChangeRequests([]);
            return;
        }
        setTradeDateChangeRequestsLoading(true);
        try {
            const res = await jobsService.getTradeDateChangeRequests(jobId);
            const list = Array.isArray(res.data)
                ? res.data
                : (res.data?.data ?? []);
            setTradeDateChangeRequests(list ?? []);
        }
        catch (err) {
            console.error("Failed to fetch trade date change requests:", err);
            setTradeDateChangeRequests([]);
        }
        finally {
            setTradeDateChangeRequestsLoading(false);
        }
    }, [jobId]);
    const fetchSites = useCallback(async () => {
        setSitesLoading(true);
        try {
            const response = await sitesService.getSites({ pageSize: 100 });
            const sites = response.data.data || [];
            setAvailableSites(sites.map((s) => ({
                id: s.id,
                siteName: s.siteName,
                address: s.address ?? null,
                suburb: s.suburb ?? null,
                state: s.state ?? null,
                postcode: s.postcode ?? null,
                buildingTypes: s.buildingTypes ?? null,
                tenancyName: s.tenancyName ?? null,
                tenancyNumber: s.tenancyNumber ?? null,
                landlord: s.landlord ?? null,
                shoppingCenter: s.shoppingCenter ?? s.contactPerson?.shoppingCenter ?? null,
            })));
        }
        catch (err) {
            console.error("Failed to load sites:", err);
        }
        finally {
            setSitesLoading(false);
        }
    }, []);
    const fetchSiteDocuments = useCallback(async () => {
        if (!job?.siteId) {
            setSiteDocuments([]);
            return;
        }
        try {
            const response = await documentsService.getDocumentsBySite(job.siteId);
            setSiteDocuments(response.data);
        }
        catch (err) {
            console.error("Failed to fetch site documents:", err);
            setSiteDocuments([]);
        }
    }, [job?.siteId]);
    const fetchJobDocuments = useCallback(async () => {
        if (!jobId) {
            setJobDocuments([]);
            return;
        }
        try {
            const response = await jobsService.getJobDocuments(jobId);
            setJobDocuments(response.data);
        }
        catch (err) {
            console.error("Failed to fetch job documents:", err);
            setJobDocuments([]);
        }
    }, [jobId]);
    const fetchJobDocumentsFromDocumentsApi = useCallback(async () => {
        if (!jobId) {
            setJobDocumentsFromDocumentsApi([]);
            return;
        }
        try {
            const response = await documentsService.getDocumentsByJob(jobId);
            setJobDocumentsFromDocumentsApi(Array.isArray(response.data) ? response.data : []);
        }
        catch (err) {
            console.error("Failed to fetch job documents from documents API:", err);
            setJobDocumentsFromDocumentsApi([]);
        }
    }, [jobId]);
    const fetchQuoteDocuments = useCallback(async () => {
        if (!job?.quoteId) {
            setQuoteDocuments([]);
            return;
        }
        try {
            const response = await documentsService.getDocumentsByQuote(job.quoteId);
            setQuoteDocuments(Array.isArray(response.data) ? response.data : []);
        }
        catch (err) {
            console.error("Failed to fetch quote documents:", err);
            setQuoteDocuments([]);
        }
    }, [job?.quoteId]);
    const fetchChecklist = useCallback(async () => {
        if (!jobId) {
            setChecklistItems([]);
            return;
        }
        setChecklistLoading(true);
        try {
            const response = await jobsService.getJobChecklist(jobId);
            setChecklistItems(response.data.data ?? []);
        }
        catch (err) {
            console.error("Failed to load checklist:", err);
            setChecklistItems([]);
        }
        finally {
            setChecklistLoading(false);
        }
    }, [jobId]);
    useEffect(() => {
        if (jobId)
            fetchSites();
    }, [jobId, fetchSites]);
    useEffect(() => {
        fetchSiteDocuments();
    }, [fetchSiteDocuments]);
    useEffect(() => {
        fetchJobDocuments();
    }, [fetchJobDocuments]);
    useEffect(() => {
        fetchJobDocumentsFromDocumentsApi();
    }, [fetchJobDocumentsFromDocumentsApi]);
    useEffect(() => {
        fetchQuoteDocuments();
    }, [fetchQuoteDocuments]);
    useEffect(() => {
        fetchChecklist();
    }, [fetchChecklist]);
    useEffect(() => {
        if (jobId)
            fetchTradeDateChangeRequests();
    }, [jobId, fetchTradeDateChangeRequests]);
    const fetchDilapidationExists = useCallback(async () => {
        if (!jobId) {
            setHasDilapidation(false);
            return;
        }
        try {
            const res = await dilapidationService.getByJobId(jobId);
            setHasDilapidation(!!res.data?.dilapidation);
        }
        catch {
            setHasDilapidation(false);
        }
    }, [jobId]);
    useEffect(() => {
        fetchDilapidationExists();
    }, [fetchDilapidationExists]);
    const tabs = useMemo(() => {
        if (hasAdditionalWorks) {
            return [
                ...BASE_TABS,
                { id: "additional-works", label: "Additional Works" },
            ];
        }
        return BASE_TABS;
    }, [hasAdditionalWorks]);
    const handleBackClick = () => {
        navigate(-1);
    };
    const handleAdditionalWorks = useCallback(async () => {
        if (!jobId)
            return;
        try {
            const result = await updateJob({ isAdditionalWork: true });
            if (result) {
                await refetchJob();
                const params = new URLSearchParams();
                params.set("mainJobId", jobId);
                if (job?.siteId)
                    params.set("siteId", job.siteId);
                navigate(`${basePath}/estimating/create-additional?${params.toString()}`);
            }
        }
        catch (err) {
            console.error("Failed to enable additional works:", err);
        }
    }, [jobId, job?.siteId, basePath, navigate, updateJob, refetchJob]);
    const handleOpenAddDocument = useCallback((defaults) => {
        setActiveTab("documents");
        setOpenAddDocumentRequest(defaults);
    }, []);
    const jobEditPayload = useMemo(() => ({
        jobId,
        job,
        jobLoading,
        jobSaving,
        jobError,
        refetchJob,
        updateJob,
        updateTradeAssignment,
        availableSites,
        sitesLoading,
        allContacts,
        refetchContacts,
        surveyorContacts,
        siteDocuments,
        jobDocuments,
        jobDocumentsFromDocumentsApi,
        quoteDocuments,
        refetchSiteDocuments: fetchSiteDocuments,
        refetchJobDocuments: fetchJobDocuments,
        refetchJobDocumentsFromDocumentsApi: fetchJobDocumentsFromDocumentsApi,
        refetchQuoteDocuments: fetchQuoteDocuments,
        checklistItems,
        checklistLoading: checklistLoading,
        refetchChecklist: fetchChecklist,
        onOpenAddDocument: handleOpenAddDocument,
        tradeDateChangeRequests,
        tradeDateChangeRequestsLoading,
        refetchTradeDateChangeRequests: fetchTradeDateChangeRequests,
        hasDilapidation,
    }), [
        jobId,
        job,
        jobLoading,
        jobSaving,
        jobError,
        refetchJob,
        updateJob,
        updateTradeAssignment,
        availableSites,
        sitesLoading,
        allContacts,
        refetchContacts,
        surveyorContacts,
        siteDocuments,
        jobDocuments,
        jobDocumentsFromDocumentsApi,
        quoteDocuments,
        fetchSiteDocuments,
        fetchJobDocuments,
        fetchJobDocumentsFromDocumentsApi,
        fetchQuoteDocuments,
        checklistItems,
        checklistLoading,
        fetchChecklist,
        handleOpenAddDocument,
        tradeDateChangeRequests,
        tradeDateChangeRequestsLoading,
        fetchTradeDateChangeRequests,
        hasDilapidation,
    ]);
    const renderTabContent = () => {
        switch (activeTab) {
            case "detail":
                return _jsx(JobDetailTab, { ...jobEditPayload });
            case "documents":
                return (_jsx(JobDocumentsPhotosTab, { jobId: jobId, job: job, jobDocuments: jobDocuments, jobDocumentsFromDocumentsApi: jobDocumentsFromDocumentsApi, siteDocuments: siteDocuments, quoteDocuments: quoteDocuments, refetchJobDocuments: fetchJobDocuments, refetchJobDocumentsFromDocumentsApi: fetchJobDocumentsFromDocumentsApi, refetchSiteDocuments: fetchSiteDocuments, refetchQuoteDocuments: fetchQuoteDocuments, openAddDocumentDefaults: openAddDocumentRequest, onOpenAddDocumentConsumed: () => setOpenAddDocumentRequest(null), refetchJob: refetchJob, updateJob: updateJob }));
            case "notifications":
                return (_jsx(JobNotificationsTab, { jobId: jobId ?? "", contactId: job?.contactId ?? "", jobNumber: job?.jobNumber, onMarkedAsRead: clearBadge, complaintNotes: job?.complaintnotes ??
                        job?.complaintNotes ??
                        null, complaintDate: job?.complaintdate ??
                        job
                            ?.complaintDate ??
                        job?.complaindate ??
                        null }));
            case "additional-works":
                return (_jsx(JobAdditionalWorksTab, { jobId: jobId, job: job, refetch: refetchJob, updateJob: updateJob, refetchJobDocumentsFromDocumentsApi: fetchJobDocumentsFromDocumentsApi, refetchJobDocuments: fetchJobDocuments }));
            default:
                return null;
        }
    };
    return (_jsxs("div", { className: styles.container, children: [_jsx(PageHeader, { userName: user?.name || "User", userEmail: user?.email || "" }), _jsx(PageHeaderWithBack, { title: "Edit Job", subtitle: `#${job?.jobNumber ?? ""}`, onBack: handleBackClick, breadcrumb: "Jobs/Edit Job" }), _jsxs("div", { className: styles.tabsAndActionsRow, children: [_jsx("nav", { className: styles.navTabs, children: _jsx("div", { className: styles.tabList, role: "tablist", children: tabs.map((tab) => (_jsxs("button", { className: `${styles.tabButton} ${activeTab === tab.id ? styles.active : ""}`, onClick: () => setActiveTab(tab.id), role: "tab", "aria-selected": activeTab === tab.id, children: [tab.label, tab.id === "notifications" && unreadCount > 0 && (_jsx("span", { className: styles.tabBadge, children: unreadCount > 99 ? "99+" : unreadCount }))] }, tab.id))) }) }), _jsx("div", { className: styles.tabActions, children: !hasAdditionalWorks && (_jsxs("button", { className: styles.actionBtnPrimary, onClick: handleAdditionalWorks, children: [_jsxs("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("line", { x1: "12", y1: "5", x2: "12", y2: "19" }), _jsx("line", { x1: "5", y1: "12", x2: "19", y2: "12" })] }), "Additional Works"] })) })] }), _jsx("div", { className: styles.contentArea, children: renderTabContent() })] }));
};
